/* eslint-disable react-hooks/exhaustive-deps */
import ListAltIcon from '@mui/icons-material/ListAlt'
import RefreshIcon from '@mui/icons-material/Refresh'
import TableChartIcon from '@mui/icons-material/TableChart'
import { debounce } from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PrivateLoader } from 'src/components/templates'
import DatePicker from 'src/components/UI/CustomUI/atoms/DatePicker'
import { Range } from 'src/components/UI/CustomUI/atoms/DatePicker/types'
import RadioGroup from 'src/components/UI/CustomUI/atoms/RadioGroup'
import { SelectInputOption } from 'src/components/UI/CustomUI/atoms/SelectInput/types'
import IconSwitch from 'src/components/UI/CustomUI/molecules/IconSwitch'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import Table from 'src/components/UI/CustomUI/organisms/Table'
import TablePagination from 'src/components/UI/CustomUI/organisms/Table/TablePagination'
import { companiesActions, employeesActions, estimatesActions, filtersActions, territoriesActions, territoryActions } from 'src/ducks/actions'
import { getAllCompaniesForMultipleFieldWithoutAllOption } from 'src/ducks/companies/selectors'
import estimateSelectionActions from 'src/ducks/estimateSelection/actions'
import { getAssignMultipleModalOpen, getSelectedEstimates } from 'src/ducks/estimateSelection/selector'
import { getEstimatesFilter } from 'src/ducks/filters/selector'
import { EstimatesFilterType } from 'src/ducks/filters/types'
import { SearchParams } from 'src/ducks/searches/types'
import { employeeIsReadOnly, getEmployees, getEmployeesForSelect, getEstimatesForTable, getTerritoriesSelector, getTotalEstimates } from 'src/ducks/selectors'
import { EMPLOYEE_ROLE_TYPE, ESTIMATE_STATUS_FILTERS, ROWS_PER_PAGE_EXTENDED } from 'src/helpers'
import { useIsXlScreen } from 'src/hooks'
import { icons } from '../../../../assets'
import { ActionsButton, Box, Button, colors, FieldsFilter, Grid, IconButton, SelectInput, TableContainer, TextField, Typography } from '../../../UI'
import ColumnBoard from './ColumnBoard'
import CreateEstimateModal from './CreateEstimateModal'
import headers from './headers'
import AssignModal from './modals/AssignModal'
import useStyles from './styles'

/*  */
const statusOptions = [
  ESTIMATE_STATUS_FILTERS.ALL,
  ESTIMATE_STATUS_FILTERS.NEW,
  ESTIMATE_STATUS_FILTERS.COMPOSING,
  ESTIMATE_STATUS_FILTERS.IN_REVIEW,
  ESTIMATE_STATUS_FILTERS.RELEASED,
  ESTIMATE_STATUS_FILTERS.EXPIRED,
  ESTIMATE_STATUS_FILTERS.DECLINED,
  ESTIMATE_STATUS_FILTERS.NOT_SUBMITTED,
  ESTIMATE_STATUS_FILTERS.PENDING_APPROVALS,
  ESTIMATE_STATUS_FILTERS.APPROVED,
  ESTIMATE_STATUS_FILTERS.OCR_REVIEW,
  ESTIMATE_STATUS_FILTERS.OCR_PROCESSING,
  ESTIMATE_STATUS_FILTERS.PROCESSING_ACH_PAYMENT,
  ESTIMATE_STATUS_FILTERS.AWAITING_FINANCING,
]

const inUseFilterOptions = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'In-Use',
    value: 'true'
  },
  {
    label: 'Non In-Use',
    value: 'false'
  },
]

const assignFilterOptions = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'Assigned',
    value: 'true'
  },
  {
    label: 'Non Assigned',
    value: 'false'
  },
]

const dueDateFilterOptions = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'Due Today',
    value: 'dueToday'
  },
]

const Estimates: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [xlScreen] = useIsXlScreen()
  const minWidthSel = xlScreen ? '156px' : '120px'

  const estimatesFilters = useSelector(getEstimatesFilter())
  const selectedEstimates = useSelector(getSelectedEstimates)
  const assignMultipleModalOpen = useSelector(getAssignMultipleModalOpen)
  const affiliatesStages = useSelector(getAllCompaniesForMultipleFieldWithoutAllOption)
  const territoriesOtions = useSelector(getTerritoriesSelector)
  const estimates = useSelector(getEstimatesForTable())
  const estimatorOptions = useSelector(getEmployeesForSelect('All Estimators'))
  const employees = useSelector(getEmployees)
  const totalEstimates = useSelector(getTotalEstimates)
  const isReadOnly = useSelector(employeeIsReadOnly)
  const tableHeaders = headers

  const initStatusIndex = useMemo(() => statusOptions.findIndex((option) => option === estimatesFilters.status), [estimatesFilters])

  const [tableLoading, setTableLoading] = useState(true)
  const [pageLoading, setPageLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [refresh, setRefresh] = useState(true)
  const [filters, setFilters] = useState(false)
  const [statusSelected, setStatusSelected] = useState<number>(initStatusIndex === -1 ? 0 : initStatusIndex)
  const [loadingCSV, setLoadingCSV] = useState(false)
  const [handleOpenMultipleEstimatesModal, setHandleOpenMultipleEstimatesModal] = useState(false)
  const [savedSelectedEstimates, setSavedSelectedEstimates] = useState<any[]>([])
  const { status, search, territory, assignedOption, estimateNumber, estimator, inUseOption, isColumnView, selectedAffiliates, dueDateRange } = estimatesFilters
  useEffect(() => {
    if (affiliatesStages.length && !selectedAffiliates && !filters) {
      updateEstimatesFilter({ selectedAffiliates: affiliatesStages })
    };
  }, [affiliatesStages])

  useEffect(() => {
    setStatusSelected(initStatusIndex === -1 ? 0 : initStatusIndex)
  }, [estimatesFilters])

  const getParams = useCallback(() => {
    const searchParams = {} as Partial<SearchParams>

    if (search) { searchParams.search = search }

    if (estimateNumber) { searchParams.publicId = estimateNumber }

    if (territory && territory?.label !== 'All Territories') { searchParams.territory = territory.key }

    if (estimator && estimator.label !== 'All Estimators') { searchParams.estimatorId = estimator.id }

    const resAffiliates = selectedAffiliates?.filter(aff => !aff.hide).reduce((acc: any, curr: any) => acc.concat(curr.key), [])
    const queryAffiliates = resAffiliates?.join(',')
    if (resAffiliates?.length !== 0 && resAffiliates && !resAffiliates?.includes('all')) { searchParams.affiliate = queryAffiliates }

    if (inUseOption?.length && inUseOption !== 'all') searchParams.currentlyInUse = inUseOption

    if (assignedOption?.length && assignedOption !== 'all') searchParams.hasEstimatorId = assignedOption

    if (dueDateRange?.startDate && dueDateRange?.endDate) {
      const startDate = dueDateRange.startDate instanceof Date ? dueDateRange.startDate : new Date(dueDateRange.startDate)
      const endDate = dueDateRange.endDate instanceof Date ? dueDateRange.endDate : new Date(dueDateRange.endDate)

      startDate.setHours(0, 0, 0, 0)

      endDate.setHours(23, 59, 59, 999)

      searchParams.dueDateStart = Math.floor(startDate.getTime() / 1000)
      searchParams.dueDateEnd = Math.floor(endDate.getTime() / 1000)
    }

    if (statusSelected !== 0) {
      const keyStatusSelected = Object.keys(ESTIMATE_STATUS_FILTERS)[statusSelected];
      searchParams.status = keyStatusSelected
    }

    return searchParams
  }, [status, search, territory, selectedAffiliates, estimator, inUseOption, assignedOption, dueDateRange, estimateNumber])

  const fetchServices = useCallback(() => {
    setTableLoading(true)
    dispatch(territoriesActions.fetchTerritories((_succ: boolean) => { setPageLoading(false) }))
    dispatch(territoryActions.fetchTerritoryManagers(`${EMPLOYEE_ROLE_TYPE.TERRITORY_SUCCESS_MANAGER}`, (_succ: boolean) => { setPageLoading(false) }))
    dispatch(companiesActions.fetchCompanies({}, (_succ: boolean) => { setPageLoading(false) }))
    dispatch(employeesActions.fetchEmployees({ page: 0, size: 150, searchParams: { role: EMPLOYEE_ROLE_TYPE.TERRITORY_ESTIMATOR, approved: true } }, (_succ: boolean) => { setPageLoading(false) }))
    //dispatch(estimatesActions.fetchEstimatesCounts({ searchParams: {} }, (_succ: boolean) => { setPageLoading(false) }))
  }, [])

  const fetchEstimates = (params: Partial<SearchParams>, page?: number, size?: number) => {
    // if (isColumnView) return;

    setTableLoading(true)
    const searchParams = params
    dispatch(
      estimatesActions.fetchEstimates({ page, size, searchParams }, (_succ: boolean) => {
        setTableLoading(false)
      })
    )

    //setCountsLoading(true)
    /*  dispatch(estimatesActions.fetchEstimatesCounts({ searchParams }, (_succ: boolean) => {
       setCountsLoading(false)
     })) */
  }

  const debouncedFetchEstimates = useCallback(
    debounce(fetchEstimates, 500),
    []
  );

  useEffect(() => {
    if (filters) {
      debouncedFetchEstimates(getParams());
    }
  }, [status, search, territory, selectedAffiliates, estimator, inUseOption, assignedOption, dueDateRange, estimateNumber])

  useEffect(() => {
    if (!isColumnView) debouncedFetchEstimates(getParams())
  }, [isColumnView])

  useEffect(() => {
    if (refresh) {
      fetchServices()
      debouncedFetchEstimates(getParams())

      // refresh for the kanban board
      if (isColumnView) {
        updateEstimatesFilter({
          ...estimatesFilters,
          refreshTrigger: (estimatesFilters.refreshTrigger || 0) + 1
        })
      }
    }
    setRefresh(false)
  }, [refresh])

  const handleChangeEstimator = (newSelected: SelectInputOption) => {
    if (!newSelected.key) {
      updateEstimatesFilter({ estimator: estimatorOptions[0] })
    } else {
      updateEstimatesFilter({ estimator: newSelected })
    }
    setFilters(true)
  }

  const handleATerritoryChange = (newSelected: SelectInputOption) => {
    if (newSelected === null) {
      updateEstimatesFilter({ territory: territoriesOtions[0] })
    } else {
      updateEstimatesFilter({ territory: newSelected })
    }
    setFilters(true)
  }

  const handleAffiliatesChange = (index: number, id: string): void => {
    setFilters(true)
    if (index === -1) { // unselect all if index is - 1
      updateEstimatesFilter({ selectedAffiliates: selectedAffiliates?.map((aff) => ({ ...aff, hide: true })) })
    } else {
      const realIndex = selectedAffiliates?.findIndex((aff) => aff.id === id)

      updateEstimatesFilter({
        selectedAffiliates: selectedAffiliates?.map((aff, i) => {
          if (i === realIndex)
            aff.hide = !aff.hide
          return aff
        })
      })
    }
  }

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const pattern = /^[a-zA-Z0-9 ,.-]+$/;
    const notSpecialCharacters = pattern.test(e.target.value);
    if (!notSpecialCharacters && e.target.value) return;
    updateEstimatesFilter({ search: e.target.value })
    setFilters(true)
  }

  const handleChangeEstimateNumber = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length <= 15) {
      updateEstimatesFilter({ estimateNumber: onlyNums })
      setFilters(true)
    }
  }

  const onResetSearchInput = (): void => {
    setFilters(false)

    setStatusSelected(0)

    updateEstimatesFilter({
      selectedAffiliates: affiliatesStages,
      estimateNumber: '',
      search: '',
      territory: territoriesOtions[0],
      estimator: estimatorOptions[0],
      inUseOption: inUseFilterOptions[0].value,
      assignedOption: assignFilterOptions[0].value,
      dueDateOption: dueDateFilterOptions[0].value,
      dueDateRange: undefined,
      status: statusOptions[0],
    })

    setFilters(true)
  }

  const onSearch = (): void => {
    fetchEstimates(getParams(), 0, 50)
  }

  const handleClickRow = (row: any, e: React.MouseEvent) => {
    if (e.type !== 'contextmenu') {
      navigate(`${row.id}`)
      e.preventDefault()
    }
  }

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Enter') {
      onSearch()
    }
  }

  const handleInUseOptionChange = (newValue: string) => {
    updateEstimatesFilter({ inUseOption: newValue })

    setFilters(true)
  }

  const handleAssignedOptionChange = (newValue: string) => {
    updateEstimatesFilter({ assignedOption: newValue })

    setFilters(true)
  }

  const handleStatusChange = (newStatus: SelectInputOption) => {
    const index = parseInt(newStatus.key)
    setStatusSelected(index || 0)
    updateEstimatesFilter({ status: statusOptions[index] || '' })

    setFilters(true)
  }

  const updateEstimatesFilter = (newFilters: Partial<EstimatesFilterType>) => {
    dispatch(
      filtersActions.setFilters({
        estimates: {
          ...estimatesFilters,
          ...newFilters
        }
      })
    )
  }

  const handleDueDateRangeChange = (newValue: Range) => {
    updateEstimatesFilter({ dueDateRange: newValue })
    setFilters(true)
  }

  const downloadCSV = (event: any) => {
    const searchParams = getParams()
    setLoadingCSV(true)
    dispatch(
      estimatesActions.downloadCSV(
        {
          searchParams,
        },
        (succ: boolean, csvUrl?: string) => {
          setLoadingCSV(false)
          succ && csvUrl && window.open(csvUrl, '_blank')
        }
      )
    )
  }

  const handleAssignMultipleModalOpen = (open: boolean) => {
    dispatch(estimateSelectionActions.setAssignMultipleModalOpen(open))
  }

  const handleOpenMultipleEstimates = () => {
    setHandleOpenMultipleEstimatesModal(false)
    savedSelectedEstimates?.forEach((estimate: any) => {
      window.open(`/estimates/${estimate}?tab=1&editMode=true`, '_blank')
    })
    setSavedSelectedEstimates([])
  }

  return (
    <div onKeyDown={handleKeyDown}>
      <PrivateLoader loading={pageLoading}>
        <Box display='flex' flexDirection='column' gap={2} className={classes.container}>
          <Grid container spacing={2} className={classes.filters}>
            <Grid item xs={1.5}>
              <SelectInput
                label='Territory:'
                onChange={handleATerritoryChange}
                options={territoriesOtions}
                value={estimatesFilters.territory || territoriesOtions[0]}
                sx={{ minWidth: minWidthSel, flex: '1' }}
              />
            </Grid>

            <Grid item xs={1.5}>
              <SelectInput
                label='Status:'
                onChange={handleStatusChange}
                options={statusOptions.map((state, idx) => {
                  return {
                    key: '' + idx,
                    label: state
                  }
                })}
                value={statusOptions[statusSelected]}
                sx={{ minWidth: minWidthSel, flex: '1' }}
              />
            </Grid>

            <Grid item xs={1.5}>
              <FieldsFilter
                flexColumn
                handleChange={handleAffiliatesChange}
                inputText='All Affiliates:'
                popupLabel=''
                options={estimatesFilters.selectedAffiliates || affiliatesStages}
                renderAsInput
                contentAlign='left'
                label='Affiliate:'
                variant='body1'
                sx={{ minWidth: minWidthSel, flex: '1' }}
                searchable={true}
                popoverSx={{ width: '378px !important' }}
                searchFieldPlaceholder='Enter Affiliate name'
                allSelection={false}
              />
            </Grid>

            <Grid item xs={2}>
              <SelectInput
                label='Estimator:'
                onChange={handleChangeEstimator}
                options={estimatorOptions}
                value={estimatesFilters.estimator || estimatorOptions[0]}
                sx={{ minWidth: minWidthSel, flex: '1' }}
              />
            </Grid>

            <Grid item xs={2}>
              <Box display='flex' flexDirection='column' justifyContent='flex-start' minWidth={xlScreen ? '230px' : '170px'} sx={{ flex: '1' }}>
                <Typography variant='h6'> Search by Address: </Typography>
                <TextField size='small' style={xlScreen ? { height: '30px' } : { height: '30px', minWidth: '50%' }} placeholder='Search By Address' value={estimatesFilters.search || ''} onChange={handleChangeSearchInput} />
              </Box>
            </Grid>
            <Grid item container flexDirection='column' justifyContent='flex-end' width='fit-content'>
              <IconSwitch
                disabled={isReadOnly}
                firstIcon={<TableChartIcon style={{ width: '16px', height: '16px' }} />}
                secondIcon={<ListAltIcon style={{ width: '16px', height: '16px' }} />}
                status={!!isColumnView}
                handleToggle={(toggleState: boolean) => {
                  updateEstimatesFilter({ isColumnView: toggleState })
                }
                }
              />
            </Grid>
            <Grid item container flexDirection='column' justifyContent='flex-end' width='fit-content'>
              <Button
                disabled={isReadOnly}
                sx={{ ml: 0.2, height: '36px' }}
                startIcon={< icons.Add />}
                variant='contained'
                onClick={() => setOpen(true)}
                fullWidth
              >
                New Estimate
              </Button>
            </Grid>
            <Grid item xs={1}>
              {!isColumnView && <>
                <ActionsButton
                  disabled={isReadOnly}
                  loading={loadingCSV}
                  icon={<icons.CloudDownload color="disabled" />}
                  iconPosition="end"
                  onClick={downloadCSV}
                  sx={{
                    minWidth: '56px!important',
                  }}
                />
                {/* <ExportCSV
                  innerRef={downloadRef}
                  headers={estimatesCSV
                    ?.slice(0, estimatesCSV?.search('\n'))
                    ?.split(',')
                    ?.map((h: any) => h)}
                  data={estimatesCSV?.slice(estimatesCSV?.search('\n') + 1)}
                  fileName={'estimates.csv'}
                  label=""
                /> */}
              </>}
            </Grid>
          </Grid>

          {/*  <StatusBoxes data={data} sx={{ textAlign: 'left', alignItems: 'left' }} loading={countsLoading} /> */}
          <Grid container spacing={2}>
            <Grid item>
              <Typography variant='h6'> In-Use Filter: </Typography>
              <RadioGroup
                options={inUseFilterOptions}
                onChange={(e, newValue) => handleInUseOptionChange(newValue)}
                value={estimatesFilters.inUseOption || inUseFilterOptions[0].value}
                labelPlacement='end'
                sx={{ border: '2px solid #EBECEF', padding: '8px', borderRadius: '8px', boxShadow: '0px 1px 1px rgba(26, 42, 85, 0.24), 0px 0px 1px rgba(26, 42, 85, 0.31)' }}
              />
            </Grid>
            <Grid item>
              <Typography variant='h6'> Assigned Filter: </Typography>
              <RadioGroup
                options={assignFilterOptions}
                onChange={(e, newValue) => handleAssignedOptionChange(newValue)}
                value={estimatesFilters.assignedOption || assignFilterOptions[0].value}
                labelPlacement='end'
                sx={{ border: '2px solid #EBECEF', padding: '8px', borderRadius: '8px', boxShadow: '0px 1px 1px rgba(26, 42, 85, 0.24), 0px 0px 1px rgba(26, 42, 85, 0.31)' }}
              />
            </Grid>
            <Grid item>
              <DatePicker
                onChange={handleDueDateRangeChange}
                value={{
                  startDate: dueDateRange?.startDate ? new Date(dueDateRange?.startDate) : undefined,
                  endDate: dueDateRange?.endDate ? new Date(dueDateRange?.endDate) : undefined
                }}
                label='Due Date Range Filter:'
                allowRange
              />
              {/* <RadioGroup
                options={dueDateFilterOptions}
                onChange={(e, newValue) => handleDueTodayOptionChange(newValue)}
                value={estimatesFilters.dueDateOption || dueDateFilterOptions[0].value}
                labelPlacement='end'
                sx={{ border: '2px solid #EBECEF', padding: '8px', borderRadius: '8px', boxShadow: '0px 1px 1px rgba(26, 42, 85, 0.24), 0px 0px 1px rgba(26, 42, 85, 0.31)' }}
              /> */}
            </Grid>
            <Grid item container alignItems='flex-end' flexWrap='nowrap' width='fit-content'>
              <Button
                variant='outlined'
                sx={{
                  width: '80px',
                  height: '34px',
                  marginTop: '5px',
                  backgroundColor: '#FFF',
                  color: colors.blue[700],
                  '&:hover': {
                    color: 'white'
                  }
                }}
                onClick={onResetSearchInput}
              >
                Clear Filters
              </Button>
              <IconButton
                style={{
                  marginLeft: '10px',
                  marginTop: '5px'
                }} onClick={() => setRefresh(true)}
              >
                <RefreshIcon />
              </IconButton>
              <TextField
                size='small'
                style={xlScreen ? {} : { minWidth: '50%' }}
                placeholder='Search By ID'
                value={estimatesFilters.estimateNumber || ''}
                onChange={handleChangeEstimateNumber}
                type='number'
              />
            </Grid>
            {isColumnView && <Grid container justifyContent='flex-end' alignItems='flex-end' item flex={1}>
              {selectedEstimates?.length && selectedEstimates?.length > 0 ? (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    dispatch(estimateSelectionActions.setAssignMultipleModalOpen(true))
                  }}
                  style={{ marginRight: '8px' }}
                >
                  Assign ({selectedEstimates?.length || 0})
                </Button>
              ) : <></>}
              <CreateEstimateModal setIsOpen={setOpen} isOpen={open} />
            </Grid>}
            {!isColumnView && <Grid container justifyContent='flex-end' alignItems='flex-end' item flex={1}>
              <CreateEstimateModal setIsOpen={setOpen} isOpen={open} />
              <TablePagination
                callToApi={(start, limit) => { fetchEstimates(getParams(), start, limit) }}
                totalItems={totalEstimates}
                rowsPerPageOptions={ROWS_PER_PAGE_EXTENDED}
              />
            </Grid>}
          </Grid>
          {!isColumnView ? (
            <TableContainer
              style={{ maxHeight: "65.5vh" }}>

              <Table
                headers={tableHeaders}
                defaultOrder='desc'
                rowAction={handleClickRow}
                callToApi={(start, limit) => { fetchEstimates(getParams(), start, limit) }}
                loading={tableLoading}
                hasPagination={false}
                onRightClickRow={handleClickRow}
                className={!xlScreen ? classes.table : ''}
                stickyHeader
                sortable
              >
                {estimates?.map(estimate => ({
                  ...estimate,
                })) ?? []}
              </Table>
            </TableContainer>
          ) : <ColumnBoard />}
          {assignMultipleModalOpen && (
            <AssignModal
              open={assignMultipleModalOpen}
              setOpen={handleAssignMultipleModalOpen}
              assigners={employees || []}
              handleClose={() => {
                handleAssignMultipleModalOpen(false)
                dispatch(estimateSelectionActions.clearSelectedEstimates())
              }}
              onAssignComplete={() => {
                setHandleOpenMultipleEstimatesModal(true)
                setSavedSelectedEstimates(selectedEstimates)
                setRefresh(true)
              }}
              estimate={{ id: 'multiple' } as any}
              estimateMultiple={selectedEstimates}
            />
          )}
          <Modal
            open={handleOpenMultipleEstimatesModal}
            setOpen={setHandleOpenMultipleEstimatesModal}
            title="Multiple Estimates"
            onClose={() => setHandleOpenMultipleEstimatesModal(false)}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <h1>You want to open each estimate in a new tab?</h1>
              <Box display="flex" flexDirection="row" gap={2} width="100%">
                <Button fullWidth variant="contained" color="primary" onClick={handleOpenMultipleEstimates}>Yes</Button>
                <Button fullWidth variant="outlined" color="info" onClick={() => {
                  setSavedSelectedEstimates([])
                  setHandleOpenMultipleEstimatesModal(false)
                }}>No</Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      </PrivateLoader>
    </div >
  )
}
export default Estimates
